import type { Plan, Style } from "@/types/api";

interface SelectionSummaryProps {
  plans: Plan[];
  styles: Style[];
  selectedPlan: string;
  selectedStyles: string[];
}

export default function SelectionSummary({
  plans,
  styles,
  selectedPlan,
  selectedStyles,
}: SelectionSummaryProps) {
  const planData = plans.find((p) => p.code === selectedPlan);
  const getStyle = (key: string) =>
    styles.find((s) => s.categoryKey === key);
  const getStyleName = (key: string) => getStyle(key)?.name || key;

  const required = planData?.styleCount || 1;
  const isAllDone = selectedStyles.length === required;

  return (
    <div className="bg-gradient-to-br from-[#FDF5EA] to-white rounded-2xl p-6 border-2 border-[#C9A96E]">
      {/* 헤더 */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C9A96E]">
          <i className="ri-file-list-3-line text-lg text-white"></i>
        </div>
        <h3 className="font-serif text-xl font-medium text-[#2D2D2D]">
          선택 내역
        </h3>
      </div>

      {/* 요금제 */}
      <div className="mb-5 pb-5 border-b border-[#C9A96E]/30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">요금제</p>
            <p className="font-serif text-lg font-medium text-[#2D2D2D]">
              {planData?.name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 mb-0.5">결제 금액</p>
            <p className="text-xl font-bold text-[#C9A96E]">
              {planData?.price.toLocaleString()}원
            </p>
          </div>
        </div>
      </div>

      {/* 진행 상태 */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-[#2D2D2D]">
            스타일 선택
          </p>
          <span
            className={`text-xs font-medium ${isAllDone ? "text-green-600" : "text-[#C9A96E]"}`}
          >
            {selectedStyles.length} / {required} 완료
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div
            className="bg-[#C9A96E] h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${(selectedStyles.length / required) * 100}%` }}
          />
        </div>
      </div>

      {/* 스타일 목록 */}
      <div className="space-y-3">
        {selectedStyles.length === 0 ? (
          <p className="text-xs text-gray-400 font-light italic text-center py-4">
            아직 선택하지 않았습니다
          </p>
        ) : (
          selectedStyles.map((styleId, idx) => (
            <div
              key={styleId}
              className="bg-white rounded-xl p-3 border border-gray-200"
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C9A96E] text-white text-xs font-bold shrink-0">
                  {idx + 1}
                </div>
                <p className="text-xs font-medium text-[#2D2D2D] truncate flex-1">
                  {getStyleName(styleId)}
                </p>
                {getStyle(styleId)?.isPremium && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold shrink-0">
                    P
                  </span>
                )}
              </div>
            </div>
          ))
        )}

        {/* 빈 슬롯 */}
        {Array.from({ length: required - selectedStyles.length }).map(
          (_, i) => (
            <div
              key={`empty-${i}`}
              className="bg-white rounded-xl p-3 border border-dashed border-gray-200"
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-gray-300 text-xs shrink-0">
                  {selectedStyles.length + i + 1}
                </div>
                <p className="text-xs text-gray-300 font-light">
                  스타일을 선택해주세요
                </p>
              </div>
            </div>
          ),
        )}
      </div>

      {/* 완료 메시지 */}
      {isAllDone && (
        <div className="mt-5 flex items-center gap-2 bg-green-50 rounded-xl p-3 border border-green-200">
          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-500 shrink-0">
            <i className="ri-check-line text-xs text-white"></i>
          </div>
          <p className="text-xs text-green-700 font-medium">
            모든 선택 완료! 다음 단계로 진행하세요.
          </p>
        </div>
      )}

      {/* 플랜 안내 */}
      <div className="mt-5 pt-5 border-t border-[#C9A96E]/20">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C9A96E] shrink-0 mt-0.5">
            <i className="ri-gift-line text-xs text-white"></i>
          </div>
          <p className="text-xs text-gray-500 font-light leading-relaxed">
            {planData?.description}
          </p>
        </div>
      </div>
    </div>
  );
}
